// ABOUTME: Desktop grid body for ShDataGrid3 — scroll container, header row, pinned shadows, virtualized rows, dnd-kit drag.
// ABOUTME: Extracted into its own memoized component so React reconciliation can skip it when unrelated parent state changes.

import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Box, Checkbox, useTheme } from "@mui/material";
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { useVirtualizer } from "@tanstack/react-virtual";

import { tokens } from "../../../theme";
import ShGridHeader3 from "./ShGridHeader3";
import ShGridRow from "./ShGridRow";
import ShGridGroupHeader from "./ShGridGroupHeader";
import ShGridAddColumn from "./ShGridAddColumn";
import { ADD_COLUMN_TRACK_PX } from "./columnsToGridTemplate";
import {
    SH_DATAGRID_DEFAULT_ROW_HEIGHT,
    SH_DATAGRID_AUTO_ROW_HEIGHT_MIN_PADDING_Y,
} from "../../../newapi/global/filter/DataGridGeneralOptions";

const HEADER_HEIGHT_PX = 32;
const CHECKBOX_TRACK_PX = 48;
const DRAG_ACTIVATION_DISTANCE_PX = 6;
// Fixed slot sizes for synthetic group-header rows. Decoupled from the user's
// data row height setting — changing compact / comfortable / fit shrinks data
// rows only; group headers always render at these sizes.
//   - First header: 40px (no breathing room above since it's the topmost row)
//   - Other headers: 80px (= 40 for the label chip + 32 top padding + 8 bottom
//     padding, giving visual separation from the previous group's last row)
const GROUP_HEADER_FIRST_PX = 48;
const GROUP_HEADER_PX = 72;

/**
 * ShGridBody
 *
 * Props (all pre-computed by parent ShDataGrid3 — this component does zero derivation):
 *   dataRows                — array of rows to render (real data or injected skeletons)
 *   visibleRuntime          — ordered runtime columns `[{ column, visible, pinned }]`
 *   resolvedColumns         — visibleRuntime joined with code column metadata, includes `pinned` field
 *   codeColumnsByField      — Map<field, codeColumn>
 *   pinnedOffsets           — { [field]: { offsetPx, side, isSeam } }
 *   gridTemplate            — grid-template-columns CSS string (WITHOUT the + add-column track)
 *   hiddenColumns           — code columns not currently in runtime (for + popover)
 *   newColumnFields         — field names flagged as "new" for the indicator dot
 *   selectedItems           — array of selected row ids
 *   visibleFieldsForSortable— array of visible field names in order (for SortableContext items)
 *   onRowClick              — ({ id, row }) => void
 *   onAddColumn             — (field: string) => void
 *   onAcknowledgeNewColumns — () => void
 *   onDragEnd               — (dndKitEvent) => void
 *   rowHeight               — number (fixed px) or the string "auto" for content-fit rows
 */
const ShGridBody = memo(function ShGridBody({
    dataRows,
    visibleRuntime,
    resolvedColumns,
    codeColumnsByField,
    pinnedOffsets,
    gridTemplate,
    hiddenColumns,
    newColumnFields,
    selectedItems,
    visibleFieldsForSortable,
    onRowClick,
    onAddColumn,
    onAcknowledgeNewColumns,
    onDragEnd,
    fixedHeader = false,
    collapsed = false,
    rowHeight = SH_DATAGRID_DEFAULT_ROW_HEIGHT,
    onToggleGroup,
    onScrollEnd,
    overscrollContain = false,
    infiniteScroll = false,
    selectable = false,
    onToggleRow,
    onToggleAll,
    isRowSelectable,
    getRowClassName,
    getRowTooltip,
}) {
    const autoRowHeight = rowHeight === "auto";
    // In auto mode tanstack/react-virtual measures each row element after render and
    // folds the real height back into the virtualizer. `estimateSize` is only the
    // initial guess used for scrollbar sizing before measurement settles.
    const estimatedRowHeight = autoRowHeight
        ? SH_DATAGRID_DEFAULT_ROW_HEIGHT
        : rowHeight;
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const effectiveGridTemplate = selectable
        ? `${CHECKBOX_TRACK_PX}px ${gridTemplate}`
        : gridTemplate;

    // Compute "all selected" state for the header checkbox
    const selectableIds = useMemo(() => {
        if (!selectable) return [];
        return dataRows
            .filter((r) => r && !r.skeleton && !r.__isGroupHeader && (!isRowSelectable || isRowSelectable(r)))
            .map((r) => r.id);
    }, [selectable, dataRows, isRowSelectable]);

    const allSelected = selectable && selectableIds.length > 0 && selectableIds.every((id) => selectedItems.includes(id));
    const someSelected = selectable && !allSelected && selectableIds.some((id) => selectedItems.includes(id));

    const scrollRef = useRef(null);
    const railRef = useRef(null);
    const headerRowRef = useRef(null);
    const headerInnerRef = useRef(null);

    const handleScroll = useCallback(
        (e) => {
            const el = e.currentTarget;
            const scrolledLeft = el.scrollLeft > 0;
            const scrolledRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
            el.dataset.scrolledLeft = scrolledLeft ? "true" : "false";
            el.dataset.scrolledRight = scrolledRight ? "true" : "false";

            // In fixed mode the header row is lifted out of the scroll container, so we
            // mirror the body's horizontal scroll by setting the header row's own
            // scrollLeft. This keeps `position: sticky` alive on pinned header cells
            // because the header row IS their scrolling ancestor — unlike the previous
            // transform-based approach which faked the scroll and broke sticky behavior.
            if (fixedHeader && headerRowRef.current) {
                headerRowRef.current.scrollLeft = el.scrollLeft;
            }

            if (onScrollEnd) {
                const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
                if (nearBottom) {
                    onScrollEnd();
                }
            }
        },
        [fixedHeader, onScrollEnd]
    );

    // When fixedHeader flips on: size the header inner to the rail width so its grid
    // template (which can include fr units) resolves against the full rail width rather
    // than the narrower visible header-row width, and sync the header row's scrollLeft
    // to the body's. Keep it in sync on rail resize.
    // When fixedHeader flips off: clear the inline width and reset scrollLeft.
    useEffect(() => {
        const innerEl = headerInnerRef.current;
        const rowEl = headerRowRef.current;
        if (!innerEl || !rowEl) return;

        if (!fixedHeader) {
            innerEl.style.width = "";
            rowEl.scrollLeft = 0;
            return;
        }

        const updateWidth = () => {
            if (!innerEl || !railRef.current) return;
            innerEl.style.width = `${railRef.current.offsetWidth}px`;
        };
        const syncScroll = () => {
            if (!rowEl || !scrollRef.current) return;
            rowEl.scrollLeft = scrollRef.current.scrollLeft;
        };

        updateWidth();
        syncScroll();

        if (!railRef.current || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(updateWidth);
        ro.observe(railRef.current);
        return () => ro.disconnect();
    }, [fixedHeader]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
        }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Grouping-aware index pre-computation, memoized on dataRows so the O(n)
    // scan only runs when the row list identity changes, not on every render.
    // Declared BEFORE useVirtualizer so `estimateSize` can reference it.
    //   - firstGroupHeaderIndex: first group header, gets a reduced top padding
    //     because there's no previous group above it.
    //   - lastDataRowIndex: last non-group-header row. In grouping mode its
    //     bottom border is removed so the rail doesn't end on a floating line.
    //   - isGroupMode: whether any group headers exist.
    //   - rowsBeforeGroupHeader: Set of data-row indices that are immediately
    //     followed by a group header. Those rows also get their bottom border
    //     removed because the next group header provides the visual break.
    const groupingIndices = useMemo(() => {
        let firstGroupHeaderIndex = -1;
        let lastDataRowIndex = -1;
        const rowsBeforeGroupHeader = new Set();
        for (let i = 0; i < dataRows.length; i++) {
            const r = dataRows[i];
            if (!r) continue;
            if (r.__isGroupHeader) {
                if (firstGroupHeaderIndex === -1) firstGroupHeaderIndex = i;
            } else {
                lastDataRowIndex = i;
                const next = dataRows[i + 1];
                if (next && next.__isGroupHeader) {
                    rowsBeforeGroupHeader.add(i);
                }
            }
        }
        return {
            firstGroupHeaderIndex,
            lastDataRowIndex,
            isGroupMode: firstGroupHeaderIndex !== -1,
            rowsBeforeGroupHeader,
        };
    }, [dataRows]);

    // Ref mirror so the virtualizer's `estimateSize` closure always reads the
    // latest groupingIndices without having to re-register the callback on
    // every render.
    const groupingIndicesRef = useRef(groupingIndices);
    groupingIndicesRef.current = groupingIndices;

    const virtualizer = useVirtualizer({
        count: dataRows.length,
        getScrollElement: () => scrollRef.current,
        // Row-aware sizing: group headers have fixed slots that don't depend
        // on the user's row-height setting, so changing compact / comfortable
        // / fit only shrinks data rows. The first group header is shorter
        // (no breathing room above), the rest get a taller slot to provide
        // visual separation from the previous group's last row.
        estimateSize: (index) => {
            const row = dataRows[index];
            if (row?.__isGroupHeader) {
                return index === groupingIndicesRef.current?.firstGroupHeaderIndex
                    ? GROUP_HEADER_FIRST_PX
                    : GROUP_HEADER_PX;
            }
            return estimatedRowHeight;
        },
        overscan: 8,
    });

    // Force a virtualizer remeasure whenever:
    //   - the row-height setting flips (compact → fit etc.), so data rows pick
    //     up the new estimate immediately.
    //   - dataRows identity changes (happens when grouping switches columns,
    //     e.g. createdAt → state). The virtualizer caches sizes per INDEX, so
    //     when the group-header positions shift, indices that used to be
    //     group headers are now data rows (or vice versa) but the cache still
    //     has the old size. Clearing it lets estimateSize rebuild per the new
    //     row types.
    useEffect(() => {
        virtualizer.measure();
    }, [rowHeight, dataRows, virtualizer]);

    const virtualItems = virtualizer.getVirtualItems();
    const totalRowsHeight = virtualizer.getTotalSize();

    return (
        <Box
            sx={{
                position: "relative",
                height: "100%",
                width: "100%",
            }}
        >
            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                data-scrolled-left="false"
                data-scrolled-right="false"
                sx={{
                    position: "relative",
                    height: "100%",
                    width: "100%",
                    overflow: "auto",
                    ...(overscrollContain && { overscrollBehavior: "contain" }),
                    background:
                        theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],

                    // Inner rail: sized to the grid template's `min-content` (the sum of
                    // the minmax minimums plus fixed-width tracks), clamped to at least
                    // 100% of the scroll container via `min-width: 100%`. Critically,
                    // `width: min-content` — NOT `max-content` — so the rail's intrinsic
                    // width comes from the STABLE column definitions, not from the
                    // widest content of whichever rows the virtualizer happens to have
                    // mounted. With `max-content`, row mounts/unmounts during vertical
                    // scroll would change the rail's max-content, fr tracks would
                    // redistribute, and columns would jump around as you scroll. With
                    // `min-content` the rail width depends only on (column template) +
                    // (parent width), both of which are stable during scroll.
                    "& .sh-grid-scroll-inner": {
                        position: "relative",
                        minWidth: "100%",
                        width: "min-content",
                    },

                    // Two-layer header structure:
                    //   - .sh-grid-header-row (outer): positioning wrapper (sticky → fixed).
                    //     In fixed mode, it has the viewport-relative visible-body-area
                    //     bounds and `overflow: hidden` so it clips the wider inner.
                    //   - .sh-grid-header-inner: the actual grid container with the column
                    //     template. Its width is 100% of the outer in sticky mode, and
                    //     explicitly set to the measured rail width (via JS ref) in fixed
                    //     mode. Fixed mode also applies transform: translateX(-scrollLeft)
                    //     via the scroll handler so columns stay aligned with body cells.
                    "& .sh-grid-header-row": {
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        height: HEADER_HEIGHT_PX,
                        width: "100%",
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                        background:
                            theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        ...(fixedHeader && {
                            position: "fixed",
                            top: 152,
                            zIndex: 300,
                            left: collapsed ? 24 : 366,
                            width: collapsed
                                ? "calc(100% - 48px)"
                                : "calc(100% - 342px - 48px)",
                            overflow: "hidden",
                            "@media screen and (max-width: 85.375em)": {
                                left: 24,
                                width: "calc(100% - 48px)",
                            },
                        }),
                    },

                    "& .sh-grid-header-inner": {
                        display: "grid",
                        gridTemplateColumns: effectiveGridTemplate,
                        height: "100%",
                        width: "100%",
                        // In fixed mode, the JS effect above overrides `width` with the
                        // measured rail width so fr units resolve against the full rail
                        // rather than the narrower visible header-row width.
                    },

                    "& .sh-grid-body-inner": {
                        position: "relative",
                        width: "100%",
                    },

                    // Layout driver: an invisible, in-flow, zero-height row inside
                    // body-inner whose sole purpose is to contribute a stable grid-template
                    // intrinsic width to body-inner (and therefore to the rail). This
                    // decouples the rail's width from both the virtualizer (rows come and
                    // go) and the header's `position: fixed` state (header goes out of
                    // flow but the driver stays in flow).
                    "& .sh-grid-layout-driver": {
                        display: "grid",
                        gridTemplateColumns: effectiveGridTemplate,
                        height: 0,
                        width: "100%",
                        visibility: "hidden",
                        pointerEvents: "none",
                    },

                    "& .sh-grid-row": {
                        background:
                            theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                        minWidth: "100%",
                    },
                    "& .sh-grid-row:hover": {
                        background: colors.bg.tertiary,
                    },
                    // "& .sh-grid-row--selected": {
                    //     background:
                    //         theme.palette.mode === "dark"
                    //             ? colors.blue[100]
                    //             : colors.primary[100],
                    // },
                    "& .sh-grid-row--skeleton": {
                        pointerEvents: "none",
                    },
                    "& .sh-grid-row.error": {
                        background: "transparent !important",
                        borderBottom: "none !important",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: "8px",
                            left: 0,
                            right: 0,
                            bottom: "8px",
                            background: colors.red[100],
                            borderRadius: 10,
                            zIndex: 0,
                        },
                        "& .sh-grid-cell": {
                            position: "relative",
                            zIndex: 1,
                        },
                        "&:hover::before": {
                            background: colors.red[200],
                        },
                    },
                    // Rows that can no longer be picked, e.g. because they are already part of the selection.
                    "& .sh-grid-row.disabled .sh-grid-cell": {
                        opacity: 0.45,
                    },
                    "& .sh-grid-row.no-checkbox .sh-grid-cell--checkbox .MuiCheckbox-root": {
                        display: "none",
                    },

                    // Cell layout:
                    //   - display: flex + min-width: 0 makes grid tracks respect their
                    //     allocated widths (without min-width: 0 flex children expand to
                    //     fit content and blow out the grid).
                    //   - Children use `flex: 1 1 0` so they fill the cell width and
                    //     absolute-positioned elements (like hover-reveal copy buttons on
                    //     the SKU column) land at the cell's right edge, not at the end
                    //     of the text — matching MUI DataGrid cell behavior.
                    //   - overflow + text-overflow + white-space on both give ellipsis.
                    "& .sh-grid-cell": {
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                        minWidth: 0,
                        overflow: "hidden",
                        background: "inherit",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 14,
                        color: colors.txt.primary,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                    },
                    "& .sh-grid-cell > *": {
                        flex: "1 1 0",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    },

                    // Auto-row-height mode. Cells wrap their content and pad an 8px
                    // (or whatever SH_DATAGRID_AUTO_ROW_HEIGHT_MIN_PADDING_Y is set to)
                    // top/bottom minimum. Vertical alignment stays at `center` (inherited
                    // from the base .sh-grid-cell rule) so short cells without renderCell
                    // don't get stranded at the top when a sibling in the row has
                    // wrapped into several lines — the row grid-stretches every cell to
                    // the tallest one, and centered alignment keeps short content
                    // visually balanced in that stretched space.
                    // Overrides the default ellipsis/nowrap rules above for both the
                    // cell and any renderCell content children so wrapping actually kicks
                    // in instead of hitting `overflow: hidden`.
                    "& .sh-grid-row--auto-height .sh-grid-cell": {
                        paddingTop: `${SH_DATAGRID_AUTO_ROW_HEIGHT_MIN_PADDING_Y}px`,
                        paddingBottom: `${SH_DATAGRID_AUTO_ROW_HEIGHT_MIN_PADDING_Y}px`,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        textOverflow: "clip",
                        overflow: "visible",
                    },
                    "& .sh-grid-row--auto-height .sh-grid-cell > *": {
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "clip",
                    },
                    "& .sh-grid-cell--pinned-left, & .sh-grid-cell--pinned-right": {
                        background:
                            theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    },
                    "& .sh-grid-row:hover .sh-grid-cell--pinned-left, & .sh-grid-row:hover .sh-grid-cell--pinned-right": {
                        background: colors.bg.tertiary,
                    },

                    "& .sh-grid-header3": {
                        background:
                            theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    },

                    // Pinned→scrollable drop-shadow. Rendered directly on the seam cells
                    // (the rightmost left-pinned and the leftmost right-pinned cell) using
                    // a large-blur, zero-vertical-offset box-shadow — the same visual trick
                    // ShDataGrid2 uses on its pinned-columns overlay, just at cell level.
                    // Always on — matches ShDataGrid2 which doesn't gate the shadow on
                    // scroll state either. z-index is bumped so the shadow draws ABOVE
                    // adjacent scrollable cells (otherwise the neighboring cell's
                    // background would paint over the shadow's horizontal bleed).
                    // clipPath clamps the vertical bleed so the shadow only extends into
                    // the scrollable area and doesn't leak back into the pinned zone.
                    "& .sh-grid-cell--seam-left, & .sh-grid-header3--seam-left": {
                        zIndex: 3,
                        boxShadow: `2px 0px 19px 0px ${
                            theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(49, 65, 95, 0.15)"
                        }`,
                        clipPath: "inset(0px -24px 0px 0px)",
                    },
                    "& .sh-grid-cell--seam-right, & .sh-grid-header3--seam-right": {
                        zIndex: 3,
                        boxShadow: `-2px 0px 19px 0px ${
                            theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(49, 65, 95, 0.15)"
                        }`,
                        clipPath: "inset(0px 0px 0px -24px)",
                    },
                }}
            >
                <div className="sh-grid-scroll-inner" ref={railRef}>
                    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
                        <SortableContext
                            items={visibleFieldsForSortable}
                            strategy={horizontalListSortingStrategy}
                        >
                            <div className="sh-grid-header-row" role="row" ref={headerRowRef}>
                                <div className="sh-grid-header-inner" ref={headerInnerRef}>
                                    {selectable && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: HEADER_HEIGHT_PX,
                                            }}
                                        >
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={someSelected}
                                                onChange={onToggleAll}
                                                sx={{
                                                    p: 0,
                                                    color: theme.palette.mode === "dark" ? colors.txt.secondary : colors.grey[300],
                                                    "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                                                        color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {resolvedColumns.map((col) => {
                                        const offset = pinnedOffsets[col.field] ?? null;
                                        return (
                                            <ShGridHeader3
                                                key={col.field}
                                                column={col}
                                                sortable={col.sortable !== false}
                                                pinnedSide={col.pinned}
                                                pinnedOffset={offset}
                                                filterino={col.filter ?? null}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </SortableContext>
                    </DndContext>
                    <div className="sh-grid-body-inner" style={{ height: totalRowsHeight }}>
                        {/*
                         * Layout driver — invisible, zero-height row with the full grid
                         * template. Establishes body-inner's intrinsic width based on the
                         * stable column definition, so the rail stays wide even when the
                         * header is position: fixed (out of flow) or the virtualizer
                         * mounts/unmounts rows with different content.
                         */}
                        <div className="sh-grid-layout-driver" aria-hidden="true">
                            {selectable && <div />}
                            {resolvedColumns.map((col) => (
                                <div key={col.field} />
                            ))}
                        </div>
                        {virtualItems.map((virtualRow) => {
                            const row = dataRows[virtualRow.index];
                            if (!row) return null;

                            // Group headers: fixed height from the estimateSize constants,
                            // independent of the user's row-height setting. Explicit
                            // `height` in rowStyle locks the DOM element to the slot
                            // size, and `measureRef` is dropped because we don't need
                            // the virtualizer to re-measure — the slot is authoritative.
                            if (row.__isGroupHeader) {
                                const isFirstGroupHeader =
                                    virtualRow.index ===
                                    groupingIndices.firstGroupHeaderIndex;
                                return (
                                    <ShGridGroupHeader
                                        key={row.id}
                                        infiniteScroll={infiniteScroll}
                                        label={row.__groupLabel}
                                        count={row.__groupCount}
                                        collapsed={row.__collapsed}
                                        isFirst={isFirstGroupHeader}
                                        onToggle={
                                            onToggleGroup
                                                ? (e) => {
                                                    e.stopPropagation();
                                                    onToggleGroup(row.__groupKey);
                                                }
                                                : (e) => {
                                                    e.stopPropagation();
                                                }
                                        }
                                        rowStyle={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: isFirstGroupHeader
                                                ? GROUP_HEADER_FIRST_PX
                                                : GROUP_HEADER_PX,
                                            transform: `translateY(${virtualRow.start}px)`,
                                        }}
                                        rowIndex={virtualRow.index}
                                        colors={colors}
                                        isDark={theme.palette.mode === "dark"}
                                    />
                                );
                            }

                            const isSelected = selectedItems.includes(row.id);
                            const isLastDataRow =
                                groupingIndices.isGroupMode &&
                                virtualRow.index === groupingIndices.lastDataRowIndex;
                            const isBeforeGroupHeader =
                                groupingIndices.isGroupMode &&
                                groupingIndices.rowsBeforeGroupHeader.has(virtualRow.index);
                            // Auto mode: omit explicit height so the row grows to its
                            // content, and hand the virtualizer a measurement ref so it
                            // can fold the real height back into the virtual layout.
                            // Fixed mode: set height from virtualRow.size so all rows
                            // render at the configured preset.
                            const rowStyle = autoRowHeight
                                ? {
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      transform: `translateY(${virtualRow.start}px)`,
                                  }
                                : {
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: virtualRow.size,
                                      transform: `translateY(${virtualRow.start}px)`,
                                  };
                            // In group mode, strip the bottom border off:
                            //   - the very last data row (nothing follows it in the rail)
                            //   - any data row immediately followed by a group header
                            //     (the header provides the visual break, no need for a
                            //     row separator underneath the last row of the group)
                            if (isLastDataRow || isBeforeGroupHeader) {
                                rowStyle.borderBottom = "none";
                            }
                            return (
                                <ShGridRow
                                    key={row.id != null ? `${row.id}-${virtualRow.index}` : virtualRow.index}
                                    row={row}
                                    rowIndex={virtualRow.index}
                                    visibleRuntime={visibleRuntime}
                                    codeColumnsByField={codeColumnsByField}
                                    pinnedOffsets={pinnedOffsets}
                                    gridTemplate={effectiveGridTemplate}
                                    onRowClick={onRowClick}
                                    isSelected={isSelected}
                                    autoHeight={autoRowHeight}
                                    measureRef={autoRowHeight ? virtualizer.measureElement : undefined}
                                    rowStyle={rowStyle}
                                    selectable={selectable}
                                    onToggleRow={onToggleRow}
                                    rowSelectable={!isRowSelectable || isRowSelectable(row)}
                                    rowClassName={getRowClassName ? getRowClassName({ row }) : undefined}
                                    rowTooltip={getRowTooltip ? getRowTooltip(row) : undefined}
                                />
                            );
                        })}
                    </div>
                </div>
            </Box>

            {/* Hover-right add-column affordance — absolute overlay, NOT a grid column */}
            <ShGridAddColumn
                hiddenColumns={hiddenColumns}
                newColumnFields={newColumnFields}
                onAdd={onAddColumn}
                onAcknowledge={onAcknowledgeNewColumns}
            />
        </Box>
    );
});

export default ShGridBody;
